import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import heroV from "../../../assets/videos/heroV.mp4";
import ContactFormModal from "../../../GlobalComponents/ContactFormModal"; // Adjust path if needed

const HeroSection = () => {
  const navigate = useNavigate();
  const [showContactForm, setShowContactForm] = useState(false);

  const handleServicesClick = () => {
    navigate("/services");
  };

  const handleContactClick = () => {
    setShowContactForm(true);
  };

  return (
    <section
      style={styles.hero}
      className="hero-section"
      aria-label="Hero section"
    >
      {/* Inject small CSS block for animations / responsive tweaks */}
      <style>{keyframeStyles}</style>
      <style>{mobileStyles}</style>

      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={styles.videoBackground}
        className="hero-video"
        aria-hidden="true"
      >
        <source src={heroV} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Decorative floating shapes */}
      <div aria-hidden="true" style={styles.decorations}>
        <svg className="float circle c1" viewBox="0 0 100 100" style={styles.svg}>
          <circle cx="50" cy="50" r="50" fill="rgba(52,152,219,0.12)" />
        </svg>
        <svg className="float circle c2" viewBox="0 0 100 100" style={styles.svg}>
          <circle cx="50" cy="50" r="50" fill="rgba(46,204,113,0.09)" />
        </svg>
        <svg className="float circle c3" viewBox="0 0 100 100" style={styles.svg}>
          <circle cx="50" cy="50" r="50" fill="rgba(241,196,15,0.08)" />
        </svg>
      </div>

      {/* Blue Overlay */}
      <div style={styles.overlay} className="hero-overlay" aria-hidden="true"></div>

      {/* Bottom Shadow */}
      <div style={styles.bottomShadow} className="hero-bottom-shadow" aria-hidden="true"></div>

      {/* Content */}
      <div style={styles.heroContent} className="hero-content">
        <h1 style={styles.title} className="hero-title">
          ZWIKO TRADING
        </h1>

        {/* Slogan with button gradient */}
        <h2 className="hero-slogan" style={styles.slogan}>
          <span className="slogan-part">Safe</span>{" "}
          <span className="slogan-part">• Reliable</span>{" "}
          <span className="slogan-part">• Professional</span>
        </h2>

        {/* Short lead paragraph */}
        <p style={styles.lead} className="hero-lead">
          We deliver tailored pest control and facility services across residential, commercial,
          and industrial sites — fast response, eco-conscious products, and long-term prevention plans.
        </p>

        <div style={styles.ctaWrap} className="hero-buttons">
          <button
            style={styles.btn}
            className="hero-button"
            onClick={handleServicesClick}
          >
            Our Services
          </button>
          <button
            style={{ ...styles.btn, ...styles.btnGhost }}
            className="hero-button"
            onClick={handleContactClick}
          >
            Get In Touch
          </button>
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={showContactForm}
        onClose={() => setShowContactForm(false)}
      />
    </section>
  );
};

export default HeroSection;

/* =========================
   Keyframes and small class styles
   ========================= */
const keyframeStyles = `
  /* Slogan - uses button gradient */
  .hero-slogan {
    margin: 0 0 1rem 0;
    font-size: clamp(1.1rem, 2.6vw, 1.6rem);
    font-weight: 700;
    letter-spacing: -0.01em;
    text-transform: none;
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    align-items: center;
    /* Button gradient */
    background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    /* Subtle glow for readability */
    text-shadow: 0 0 20px rgba(52,152,219,0.5), 0 0 40px rgba(44,62,80,0.3);
  }

  /* Floating decorative shapes animation */
  .float {
    position: absolute;
    width: 220px;
    height: 220px;
    opacity: 0.9;
    filter: blur(6px);
    pointer-events: none;
    will-change: transform;
  }
  .c1 { top: 8%; left: 6%; animation: float1 12s ease-in-out infinite; }
  .c2 { bottom: 14%; right: 6%; animation: float2 14s ease-in-out infinite; }
  .c3 { top: 18%; right: 18%; animation: float3 18s ease-in-out infinite; }

  @keyframes float1 {
    0% { transform: translateY(0) translateX(0) scale(1); }
    50% { transform: translateY(-18px) translateX(8px) scale(1.05); }
    100% { transform: translateY(0) translateX(0) scale(1); }
  }
  @keyframes float2 {
    0% { transform: translateY(0) translateX(0) scale(1); }
    50% { transform: translateY(-24px) translateX(-8px) scale(0.98); }
    100% { transform: translateY(0) translateX(0) scale(1); }
  }
  @keyframes float3 {
    0% { transform: translateY(0) translateX(0) scale(1); }
    50% { transform: translateY(-12px) translateX(6px) scale(1.02); }
    100% { transform: translateY(0) translateX(0) scale(1); }
  }

  /* small responsive tweaks */
  @media (max-width: 768px) {
    .hero-slogan { font-size: 1.1rem; }
  }
`;

/* =========================
   Mobile Styles
   ========================= */
const mobileStyles = `
  @media (max-width: 768px) {
    .hero-content {
      padding: 1rem !important;
      margin-top: 1.6rem !important;
    }
    
    .hero-title {
      font-size: 1.8rem !important;
      margin-bottom: 2rem !important;
    }
    
    .hero-lead {
      font-size: 0.95rem !important;
      margin-bottom: 1rem !important;
      padding: 0 0.5rem !important;
    }
    
    .hero-buttons {
      flex-direction: column !important;
      gap: 0.6rem !important;
    }
    
    .hero-button {
      width: 100% !important;
      max-width: 320px !important;
      margin: 0 auto !important;
    }

    .hero-bottom-shadow {
      height: 60px !important;
    }
  }
  
  @media (max-width: 480px) {
    .hero-title {
      font-size: 1.4rem !important;
      margin-bottom: 1.6rem !important;
    }
    
    .hero-lead {
      font-size: 0.9rem !important;
    }
    
    .hero-section {
      min-height: 66vh !important;
      height: auto !important;
      padding: 1rem 0 !important;
    }

    .hero-bottom-shadow {
      height: 40px !important;
    }
  }
`;

/* =========================
   Styles object (inline)
   ========================= */
const styles = {
  hero: {
    position: "relative",
    height: "80vh",
    minHeight: 520,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    color: "#ffffff",
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },

  videoBackground: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    zIndex: 0,
  },

  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(135deg, rgba(10,23,34,0.28) 0%, rgba(52, 152, 219, 0.14) 60%, rgba(10,23,34,0.35) 100%)",
    zIndex: 1,
    pointerEvents: "none",
  },

  bottomShadow: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "80px",
    background: "linear-gradient(to top, rgba(10,23,34,0.75) 0%, transparent 100%)",
    zIndex: 2,
    pointerEvents: "none",
  },

  decorations: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    pointerEvents: "none",
  },

  svg: {
    maxWidth: 240,
  },

  heroContent: {
    position: "relative",
    zIndex: 4,
    maxWidth: 980,
    padding: "2rem 2rem",
    textAlign: "center",
    color: "white",
    textShadow: "0 6px 18px rgba(0,0,0,0.55)",
  },

  title: {
    fontSize: "clamp(2rem, 5vw, 3.5rem)",
    margin: 0,
    marginBottom: "1rem",
    fontWeight: 800,
    letterSpacing: "-0.03em",
    lineHeight: 1.05,
    color: "#ffffff",                /* pure white */
    textShadow: "0 2px 10px rgba(0,0,0,0.6)",
  },

  slogan: {
    marginBottom: "1rem",
  },

  lead: {
    fontSize: "1.05rem",
    margin: "0 auto 1.25rem",
    maxWidth: 820,
    color: "rgba(255,255,255,0.95)",
    opacity: 0.95,
    lineHeight: 1.5,
  },

  ctaWrap: {
    display: "flex",
    gap: "1rem",
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: "1rem",
  },

  btn: {
    background: "linear-gradient(135deg, #2c3e50 0%, #3498db 100%)",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "1rem",
    transition: "all 0.25s ease",
    boxShadow: "0 8px 30px rgba(52, 152, 219, 0.18)",
  },

  btnGhost: {
    background: "transparent",
    border: "2px solid rgba(255,255,255,0.12)",
    color: "white",
    backgroundColor: "rgba(255,255,255,0.04)",
    backdropFilter: "blur(6px)",
  },
};